import { useState } from "react"
import styled from "styled-components"

import { LeakDetectionIcon } from "@minuthq/meatball-ui-icons"

import { UpgradeBlockerDialog } from "src/components/FeatureBlockers/UpgradeBlockerDialog"
import {
  CardStatusRow,
  MonitoringCard,
  StatusLeft,
} from "src/components/Homes/HomeDetails/Overview/MonitoringCard"
import { isWaterLeakDetectionDevice } from "src/data/devices/logic/deviceLogic"
import { TDevice } from "src/data/devices/types/deviceTypes"
import { useFeatureAvailability } from "src/data/featureAvailability/logic/useFeatureAvailability"
import { THome, TWaterLeakDetection } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchHomeWaterLeakDetection } from "src/data/organizations/queries/homeQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"
import AlertingIcon from "src/ui/icons/important-outlined.svg"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { spacing } from "src/ui/spacing"
import { formatDateAsTime } from "src/utils/l10n"

import { WaterLeakDetectionPopover } from "./WaterLeakDetectionPopover"

type THomeData = Pick<
  THome,
  "home_id" | "violations" | "timezone" | "address" | "water_leak_detection"
>

export function WaterLeakDetectionCard({
  waterLeakDetection,
  toggleAllowed,
  devices,
  homeData,
}: {
  waterLeakDetection: TWaterLeakDetection
  toggleAllowed: boolean
  devices: TDevice[]
  homeData: THomeData
}) {
  const { t, langKeys } = useTranslate()

  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)

  const featureAvailabilityLeakDetection = useFeatureAvailability({
    feature: "water_leak_detection",
  })
  const isEnabled = waterLeakDetection.mode === "enabled"
  const LeakDetectionFeatureAvailableInPlan =
    featureAvailabilityLeakDetection.available &&
    featureAvailabilityLeakDetection.ready

  function handleClickToggle(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    LeakDetectionFeatureAvailableInPlan
      ? setOpen(true)
      : setShowUpgradeDialog(true)

    setAnchorEl(event.currentTarget)
  }

  const tooltipText = toggleAllowed ? "" : t(langKeys.not_enough_permissions)

  function handleClose() {
    setOpen(false)
    setAnchorEl(null)
  }
  return (
    <div>
      <MonitoringCard
        titleSlots={[
          <StatusLeft
            key="leak-detection-status-left"
            icon={<LeakDetectionIcon width={32} height={32} />}
            title={t(langKeys.leak_detection_title)}
            statusContents={isEnabled ? t(langKeys.on) : t(langKeys.off)}
            isOn={isEnabled}
            showUpgradeButton={
              !featureAvailabilityLeakDetection.available ||
              !featureAvailabilityLeakDetection.ready
            }
          />,
          <MTooltip
            key="leak-detection-tooltip"
            title={tooltipText}
            placement={"top"}
          >
            <div>
              <MButton
                variant="subtle"
                size="small"
                key="right"
                onClick={handleClickToggle}
                disabled={!toggleAllowed}
                loading={!featureAvailabilityLeakDetection.ready}
              >
                {t(langKeys.manage)}
              </MButton>
            </div>
          </MTooltip>,
        ]}
        cardFooter={{
          showTopBorder: waterLeakDetection.status === "alerting",
          component: (
            <WaterLeakDetectionFooter
              status={waterLeakDetection.status}
              devices={devices}
              homeData={homeData}
            />
          ),
        }}
      />

      {open && (
        <WaterLeakDetectionPopover
          homeId={homeData.home_id}
          anchorEl={anchorEl}
          violations={homeData.violations}
          state={waterLeakDetection.mode}
          onClose={handleClose}
        />
      )}

      <UpgradeBlockerDialog
        context="water_leak_detection"
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
      />
    </div>
  )
}

function WaterLeakDetectionFooter({
  status,
  devices,
  homeData,
}: {
  status: TWaterLeakDetection["status"]
  devices: TDevice[]
  homeData: THomeData
}) {
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()
  const clockType = useGetUser().clock_type

  const patchWld = usePatchHomeWaterLeakDetection({ orgId })

  function handleDismiss() {
    patchWld.mutate({
      path: { home_id: homeData.home_id },
      body: { status: "idle" },
    })
  }

  if (status === "alerting") {
    const alertTime = formatDateAsTime({
      date: homeData.water_leak_detection?.updated_at || "",
      clockType,
      excludeSeconds: true,
      timezone: homeData.timezone,
    })

    // TODO: Show only alerting wld devices MON-1492
    const wldDevices = devices.filter((device) =>
      isWaterLeakDetectionDevice(device)
    )

    return (
      <BodyBox>
        <CardStatusRow
          icon={<AlertingIcon width={24} />}
          subtitle={t(langKeys.water_leak_detection_title)}
          body={
            <StatusColumn>
              <StatusColumnRow>
                <div>{alertTime}</div>
                <div>&#x2022;</div>
                <div>
                  {wldDevices.map((device) => device.description).join(", ")}
                </div>
              </StatusColumnRow>
            </StatusColumn>
          }
          color="emergency"
        />

        <ActionColumn>
          <MButton onClick={handleDismiss} variant="secondary">
            {t(langKeys.water_leak_detection_resolved)}
          </MButton>
        </ActionColumn>
      </BodyBox>
    )
  }

  return null
}

const BodyBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.L} ${spacing.XS2};

  & > div:first-child {
    flex: 1 1 auto;
  }
`

const StatusColumn = styled.div`
  display: grid;
  gap: ${spacing.XS2};
`

const StatusColumnRow = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  gap: ${spacing.XS2};
`

const ActionColumn = styled.div`
  align-self: center;
`
